import {
	changeAlphaHSL,
	COMPARISON_COMMUNITY_COLOR,
	convertColorToHSL,
	FITS_ALL_FILTERS_COLOR,
	FULL_STATE_NAME_TO_ABBREVIATION,
	getColorsHSLA,
	SELECTED_COMMUNITY_COLOR,
} from "../../DataManagers/utilsColorTheme";

import DataManager from '../../DataManagers/DataManager'
import {
	LEGEND_TYPE,
	FILTER_TYPE, STATE_MASK_ID, STATE_MASK_STATE_COLUMN_NAME, UNIQUE_ID_FOR_ATTR_DATA,
} from "../../constants";
import RURAL_DEFINITION from '../../content/rural-definition/rural-definition'

import {createSteps, tableWithSameValue, arrayRange} from "../../utils";
import {filterIsActive} from "../selectors";

const STATE_OUTLINE_COLOR = "hsla(0,0%,50.2%,0.9)"
const TRANSPARENT_MAPBOX_PAINT_PROPERTIES = {
	"fill-color": getColorsHSLA("TRANSPARENT"),
	"fill-outline-color": getColorsHSLA("TRANSPARENT")
}

const getMapboxBoundsFiltersForOneFilter = (fieldName, filterBounds, colorValuesWhichDontFitTheFilters = false) => ([
	["<", ["to-number", ["get", fieldName]], filterBounds[0]],
	colorValuesWhichDontFitTheFilters ? getColorsHSLA("CORI_MASK") : getColorsHSLA("TRANSPARENT"),
	[">", ["to-number", ["get", fieldName]], filterBounds[1]],
	colorValuesWhichDontFitTheFilters ? getColorsHSLA("CORI_MASK") : getColorsHSLA("TRANSPARENT"),
])

const getSelectedCommunityStyle = ({geoIdForSelectedRegion = undefined}, flags = {isComparisonRegion: false}) =>  {
	if (geoIdForSelectedRegion === undefined) return []
	return [
		["==", ["get", UNIQUE_ID_FOR_ATTR_DATA], geoIdForSelectedRegion],
		flags.isComparisonRegion ? COMPARISON_COMMUNITY_COLOR : SELECTED_COMMUNITY_COLOR,
	]
}

const getMapboxStyleForSelectedCommunities = (selectedRegionInfo, comparisonRegionInfo) => {
	return [
		...getSelectedCommunityStyle(selectedRegionInfo),
		...getSelectedCommunityStyle(comparisonRegionInfo, {isComparisonRegion: true})
	]
}

// TODO: use selectors, don't deconstruct object (which we'll change)
const getMapboxBoundsFilters = (fieldFilterBounds, primaryFilter, applyAllFilters, selectedRegionInfo = {}, comparisonRegionInfo = {}) => {
	const mapboxBoundsFiltersForAllFilters = Object.keys(fieldFilterBounds)
		.filter(fieldName => (applyAllFilters && filterIsActive(fieldName, fieldFilterBounds)) || fieldName === primaryFilter.fieldName)
		.reduce((mapboxFilters, fieldName) => {
			const filterBounds = fieldFilterBounds[fieldName].filterBounds
			return [...mapboxFilters, ...getMapboxBoundsFiltersForOneFilter(fieldName, filterBounds)]
		}, [])
	return [...getMapboxStyleForSelectedCommunities(selectedRegionInfo, comparisonRegionInfo), ...mapboxBoundsFiltersForAllFilters]
}


const getSelectedAndComparisonHighlightingMapboxColorExpression = (selectedUSStatesToFilterWithin, colorExpressionForNonMaskedOutStates, stateColumnName = "st_name", maskOutStates = false, selectedRegionInfo = {}, comparisonRegionInfo = {}) => {
	const mapboxStyleForSelectedCommunities = getMapboxStyleForSelectedCommunities(selectedRegionInfo, comparisonRegionInfo)
	
	return mapboxStyleForSelectedCommunities.length
		? ['case', ...mapboxStyleForSelectedCommunities, colorExpressionForNonMaskedOutStates]
		: colorExpressionForNonMaskedOutStates
}
const getSelectedAndComparisonHighlightingMapboxColorProperties = (selectedUSStatesToFilterWithin, colorExpressionForNonMaskedOutStates, stateColumnName, maskOutStates = false, selectedRegionInfo = {}, comparisonRegionInfo = {}) => {
	return {
		"fill-color": getSelectedAndComparisonHighlightingMapboxColorExpression(selectedUSStatesToFilterWithin, colorExpressionForNonMaskedOutStates, stateColumnName, maskOutStates, selectedRegionInfo, comparisonRegionInfo),
		"fill-outline-color": getSelectedAndComparisonHighlightingMapboxColorExpression(selectedUSStatesToFilterWithin, STATE_OUTLINE_COLOR, stateColumnName, maskOutStates, selectedRegionInfo, comparisonRegionInfo)
	}
}


const getStateMaskedMapboxColorExpression = (selectedUSStatesToFilterWithin, colorExpressionForNonMaskedOutStates, stateColumnName = "st_name") => {
	const maskedStates = Object.keys(FULL_STATE_NAME_TO_ABBREVIATION)
		.filter(stateName => !selectedUSStatesToFilterWithin.includes(stateName))
	return [
		"match",
		["get", stateColumnName],
		maskedStates, getColorsHSLA("CORI_MASK"),
		// Fallback:
		colorExpressionForNonMaskedOutStates
	]
}
const getStateMaskedMapboxColorProperties = (selectedUSStatesToFilterWithin, colorExpressionForNonMaskedOutStates, stateColumnName, showStartScreen) => {
	return {
		"fill-color": showStartScreen ? getColorsHSLA("TRANSPARENT") : getStateMaskedMapboxColorExpression(selectedUSStatesToFilterWithin, colorExpressionForNonMaskedOutStates, stateColumnName),
		"fill-outline-color": showStartScreen ? getColorsHSLA("TRANSPARENT"):  getStateMaskedMapboxColorExpression(selectedUSStatesToFilterWithin, STATE_OUTLINE_COLOR, stateColumnName)
	}
}

export const _generatePaintProperty = (fieldFilterBounds, stateName, applyAllFilters, selectedUSStatesToFilterWithin, currentSceneIndex, primaryFilter, selectedRegionInfo, comparisonRegionInfo) => {
	const fallbackPaintProperties = getSelectedAndComparisonHighlightingMapboxColorProperties(selectedUSStatesToFilterWithin, getColorsHSLA("TRANSPARENT"), undefined, selectedRegionInfo, comparisonRegionInfo)
	const {fieldName, filterSteps, filterBounds, filterCategories, filterType} = primaryFilter || {}

	if (applyAllFilters) {
		const boundsFilter = getMapboxBoundsFilters(fieldFilterBounds, primaryFilter, applyAllFilters, selectedRegionInfo, comparisonRegionInfo)
		const paint = boundsFilter.length ? ["case", ...boundsFilter, FITS_ALL_FILTERS_COLOR] : FITS_ALL_FILTERS_COLOR
		return getSelectedAndComparisonHighlightingMapboxColorProperties(selectedUSStatesToFilterWithin, paint, undefined, undefined, selectedRegionInfo, comparisonRegionInfo)
	}
	if (!filterBounds && !applyAllFilters) return fallbackPaintProperties

	const filterColors = DataManager.getVizColorThemeForCurrentSlide(currentSceneIndex).map(color => changeAlphaHSL(color, 0.7))

	switch (filterType) {
		case FILTER_TYPE.NUMERIC:
			// generate a choropleth
			const paintSteps = ["step", ["to-number", ["get", fieldName]]]
			paintSteps.push(getColorsHSLA("TRANSPARENT"))
			for (var i=0; i<filterSteps.length; i++) {
				paintSteps.push(filterSteps[i])
				const isWithinStepBounds = (i < (filterSteps.length - 1)) || filterSteps.length === 1
				paintSteps.push(isWithinStepBounds ? filterColors[i] : getColorsHSLA("TRANSPARENT"))
			}
			const boundsFilter = getMapboxBoundsFilters(fieldFilterBounds, primaryFilter, applyAllFilters, selectedRegionInfo, comparisonRegionInfo)
			const filteredChloroplethColor = ["case", ...boundsFilter, paintSteps]

			return getSelectedAndComparisonHighlightingMapboxColorProperties(selectedUSStatesToFilterWithin, filteredChloroplethColor, undefined, undefined, selectedRegionInfo, comparisonRegionInfo)
		case FILTER_TYPE.CATEGORICAL:
			const length = Math.min(filterColors.length, filterCategories.length)
			const range = arrayRange(length)

			const categoryFilters = range.reduce( (filters, i) => {
				const newFilter = [["==", ["get", fieldName], filterCategories[i]], filterColors[i]]
				filters.push(...newFilter)
				return filters
			// }, ["case", ...stateFilter])
			}, ["case"])

			categoryFilters.push(getColorsHSLA("TRANSPARENT")) // default
			return getSelectedAndComparisonHighlightingMapboxColorProperties(selectedUSStatesToFilterWithin, categoryFilters, undefined, undefined, selectedRegionInfo, comparisonRegionInfo)
		default:
			return fallbackPaintProperties
	}
}

export const getRuralOptionsColumnName = cartoLayerIdOfSelectedDefinition => cartoLayerIdOfSelectedDefinition.replace('_by_state', '')

const _generateRuralPaintProp = (cartoLayerIdOfSelectedDefinition, selectedFilterOptionIndexesForSelectedDefinition, layerId, slideIsFocusedOnRuralDefinitions) => {
	const maskColor = slideIsFocusedOnRuralDefinitions ? "gray" : getColorsHSLA("CORI_MASK")
	if (layerId !== cartoLayerIdOfSelectedDefinition || selectedFilterOptionIndexesForSelectedDefinition.length === 0)
		return {"fill-color": getColorsHSLA("TRANSPARENT")}

	// We're storing indexes, so convert to values
	const ruralDefinitionObject = RURAL_DEFINITION[cartoLayerIdOfSelectedDefinition]
	const actualFilterValues = selectedFilterOptionIndexesForSelectedDefinition.map(index => ruralDefinitionObject.allFilterOptions[index])

	const filters = actualFilterValues.reduce((filters, value) => {
		const newFilter = [["==", ["get", getRuralOptionsColumnName(cartoLayerIdOfSelectedDefinition)], value], maskColor]
		return [...filters, ...newFilter]
	}, ["case"])
	filters.push(getColorsHSLA("TRANSPARENT"))

	return {"fill-color": filters}
}

const _isValidNumericFilter = filter => {
	if (filter === null || filter === undefined || filter.filterType !== FILTER_TYPE.NUMERIC)
		return false

	const {filterColors, filterSteps} = filter
	return (filter.fieldName && filterColors && filterColors.length > 0 && filterSteps && filterSteps.length > 0)
}

const _isValidCategoricalFilter = filter => {
	if (filter === null || filter === undefined || filter.filterType !== FILTER_TYPE.CATEGORICAL)
		return false

	const {filterColors, filterCategories} = filter
	return (filter.fieldName && filterColors && filterColors.length > 0 && filterCategories.length > 0)
}

export const _setStylingForVisualizedDataLayer = (layer,
																									_newSceneIndex,
																									selectedRegionInfo,
																									comparisonRegionInfo,
																									fieldFilterBounds,
																									primaryFilter,
																									showFiltersOnMap,
																									mapboxLayersReduxState,
																									cartoLayerIdOfSelectedGeography,
																									selectedUSStatesToFilterWithin,
																									applyAllFilters,
																									showStartScreen) => {
	const layerId = layer.get('id'); // get layerId

	// update location
	DataManager.updateLocationForStoryScenes(selectedRegionInfo)
	const MAP_MENU_OPT = DataManager.getMapMenuOptions()

	// update paint properties for layer if necessary
	const newScene = DataManager.getStorySceneAtIndex(_newSceneIndex)
	const layerInCurrentScene = newScene.getLayerById(layerId)
	const slideIsFocusedOnRuralDefinitions = newScene.mapMenuOptionToOpenOnSceneStart === MAP_MENU_OPT.RURAL_DEFINITION_MENU

	if (layerInCurrentScene && layerInCurrentScene.isStyled)
		return layer.set('paint', layerInCurrentScene.paint)

	if (layerInCurrentScene && layerId === cartoLayerIdOfSelectedGeography) {
		const displayPrimaryFilter = showFiltersOnMap && (_isValidNumericFilter(primaryFilter) || _isValidCategoricalFilter(primaryFilter))
		if (applyAllFilters || displayPrimaryFilter) {
			const paintProp = _generatePaintProperty(fieldFilterBounds, selectedRegionInfo.stateName, applyAllFilters, selectedUSStatesToFilterWithin, _newSceneIndex, primaryFilter, selectedRegionInfo, comparisonRegionInfo)
			return layer.set('paint', paintProp)
		} else {
			const fallbackColorLessPaintProperties = getSelectedAndComparisonHighlightingMapboxColorProperties(selectedUSStatesToFilterWithin, getColorsHSLA("TRANSPARENT"), undefined, undefined, selectedRegionInfo, comparisonRegionInfo)
			return layer.set('paint', fallbackColorLessPaintProperties)
		}
	} else if (Object.keys(RURAL_DEFINITION).includes(layerId)) {
		const {selectedFilterOptionIndexesForEachRuralDefinition, selectedRuralDefinition} = mapboxLayersReduxState
		const selectedFilterOptionIndexesForSelectedDefinition = selectedFilterOptionIndexesForEachRuralDefinition[selectedRuralDefinition]
		return layer.set('paint', _generateRuralPaintProp(selectedRuralDefinition, selectedFilterOptionIndexesForSelectedDefinition, layerId, slideIsFocusedOnRuralDefinitions))
	} else if (layerId === STATE_MASK_ID) {
		if (!layerInCurrentScene) return layer.set('paint', TRANSPARENT_MAPBOX_PAINT_PROPERTIES)
		return layer.set('paint', getStateMaskedMapboxColorProperties(selectedUSStatesToFilterWithin, getColorsHSLA("TRANSPARENT"), STATE_MASK_STATE_COLUMN_NAME, showStartScreen))
	}
	return layer
}

export const getMapboxFilters = (layers, _newSceneIndex) => {
	const activeMapboxFilters = []

	const cartoLayerLookup = tableWithSameValue(DataManager.getLayerNames(), true)
	const scene = DataManager.getStorySceneAtIndex(_newSceneIndex)
	
	layers.forEach(l => {
		const lid = l.get('id')
		const cartoLayer = DataManager.getCartoDataForLayerId(lid)
		const layer = cartoLayer && scene.getLayerById(lid)
		
		if (layer && lid in cartoLayerLookup && !cartoLayer.legendIgnore) {
			var steps = null
			var colors = []
			if (layer.fieldBins && layer.fieldRange)
				steps = createSteps({numSteps: layer.fieldBins, min: layer.fieldRange[0], max: layer.fieldRange[1]})
			
			if (layer.color)
				colors = layer.color.fillBins ? layer.color.fillBins : [layer.color.fillColor]
			else if (cartoLayer.fillColor)
				colors = [cartoLayer.fillColor]
			else if (cartoLayer.lineColor)
				colors = [cartoLayer.lineColor]
			
			colors = colors.map(c => convertColorToHSL(c))
			
			activeMapboxFilters.push({
				dataLayer: layer.dataLayer,
				fieldLabel: layer.fieldName || cartoLayer.displayName,
				filterColors: colors.map(c => changeAlphaHSL(c, 1)),
				filterSteps: steps,
				legendType: (colors.length >= 2) ? LEGEND_TYPE.BINS : ((cartoLayer.iconImage) ? LEGEND_TYPE.ICON : LEGEND_TYPE.SOLID),
				iconImage: cartoLayer.iconImage
			})
		}
	})

	// organize
	activeMapboxFilters.sort((a,b) => {
		return a.legendType > b.legendType ? 1 : ((b.legendType > a.legendType) ? -1 : 0)
	})

	return activeMapboxFilters
}
